"use client";

import { useEffect, useState } from "react";
import { DiscountCTAProps } from "./types";
import { Button } from "@/components/ui/button";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/icons";
import { BadgeIcon } from "@/components/ui/badgeIcon";
import { cn } from "@/lib/utils";

import {
  getCheckoutURL,
  GetSmarterCourseDetails,
} from "@/lib/getSmarter/utils";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import newrelic from "@/lib/monitoring/newrelic";

export default function DiscountCTA({
  courseSlug,
  buttonConfiguration,
  className,
  externalIdentifier,
}: DiscountCTAProps) {
  const [getSmarterPriceData, setGetSmarterPriceData] =
    useState<GetSmarterCourseDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPricingData = async () => {
      if (!courseSlug) return;

      try {
        setLoading(true);

        const response = await fetch(
          `/api/get-pricing?courseSlug=${encodeURIComponent(courseSlug)}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch pricing data");
        }

        const data = await response.json();
        setGetSmarterPriceData(data.pricingData);
      } catch (err) {
        newrelic.noticeError(err as Error, {
          courseSlug,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, [courseSlug]);
  interface CurrencyMap {
    [key: string]: string;
  }

  const currencySymbols: CurrencyMap = {
    USD: "$", // US Dollar
    GBP: "£", // British Pound
    ZAR: "R", // South African Rand
    EUR: "€", // Euro
    AED: "د.إ", // UAE Dirham
    SGD: "S$", // Singapore Dollar
    HKD: "HK$", // Hong Kong Dollar
    SAR: "ر.س", // Saudi Riyal
    INR: "₹", // Indian Rupee
    CAD: "CA$", // Canadian Dollar
    CHF: "CHF", // Swiss Franc
    AUD: "AU$", // Australian Dollar
  };

  const webElement: ElementClicked["webElement"] = {
    position: "1",
    elementType: ElementType.Button,
    location: "checkout-cta-header",
    htmlId: "GS-checkout",
    text: hyphenateForTagular(
      buttonConfiguration?.buttonText ??
        `Enroll for ${buttonConfiguration?.discountPercentage}% off`,
    ),
    name: "checkout-cta",
  };

  const { elementClicked } = useElementEvents({
    webElement,
  });

  const iconContent =
    buttonConfiguration?.iconText ??
    "Offer does not apply to degree programs. Offer cannot be combined with other discount codes. To redeem the offer for verified courses, Professional Certificates, XSeries, MicroBachelors® Programs, and MicroMasters® Programs.";

  const onClickCheckout = () => {
    elementClicked();
    window.location.href = checkoutURL;
  };

  const checkoutURL = getSmarterPriceData
    ? getCheckoutURL(
        getSmarterPriceData,
        buttonConfiguration?.couponCode ?? "",
        externalIdentifier,
      )
    : "#";

  // Show loading state with skeleton
  if (loading) {
    return (
      <div
        className={cn(
          "w-full text-center rounded-[4px] p-4 bg-putty-100 border border-putty-400",
          className,
        )}
      >
        {/* Skeleton for headline */}
        <div className="whitepace-nowrap w-full align-top inline-block mb-2">
          <div className="h-6 bg-gray-300 rounded animate-pulse w-3/4 mx-auto mb-2"></div>
          <div className="h-4 bg-gray-300 rounded animate-pulse w-16 mx-auto"></div>
        </div>

        {/* Skeleton for pricing */}
        <div className="text-primary-500 whitespace-nowrap my-4 w-full">
          <div className="h-5 bg-gray-300 rounded animate-pulse w-1/3 mx-auto mb-1"></div>
          <div className="h-6 bg-gray-300 rounded animate-pulse w-1/2 mx-auto"></div>
        </div>

        {/* Skeleton for button */}
        <div className="h-10 bg-gray-300 rounded animate-pulse w-full my-2"></div>

        {/* Skeleton for fine print */}
        <div className="h-3 bg-gray-300 rounded animate-pulse w-5/6 mx-auto"></div>
      </div>
    );
  }

  // Don't render if no pricing data and not loading
  if (!getSmarterPriceData) return <></>;

  const discountedPrice = buttonConfiguration?.discountPercentage
    ? (
        Number(getSmarterPriceData.price) *
        (1 - buttonConfiguration?.discountPercentage / 100)
      ).toFixed(2)
    : Number(getSmarterPriceData.price).toFixed(2);

  // Format number with thousands separators (e.g., 1000 -> 1,000)
  const formatNumber = (num: string | number) => {
    return Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const pillText = `${buttonConfiguration?.discountPercentage}% off`;
  return (
    <div
      className={cn(
        "w-full text-center rounded-[4px] p-4 bg-putty-100 border border-putty-400",
        className,
      )}
    >
      <div className="whitepace-nowrap w-full align-top inline-block">
        <div className="w-auto text-primary-500 font-bold elm-h4">
          {buttonConfiguration?.headlineText ??
            "Claim this special offer today!"}{" "}
          {buttonConfiguration?.showPill && (
            <>
              <BadgeIcon color="primaryLight" icon="sale" iconSize={12}>
                {pillText}
              </BadgeIcon>
              <span className="mt-1 ml-1">
                <Tooltip content={iconContent}>
                  <Icon
                    svgProps={{ width: "15px", height: "15px" }}
                    type="outline-warning-circle"
                  />
                </Tooltip>
              </span>
            </>
          )}
        </div>
      </div>

      <div className="text-primary-500 whitespace-nowrap my-4 w-full">
        {buttonConfiguration?.showStrikethroughPrice && (
          <>
            <span className="elm-h6 font-bold text-gray-500 line-through mr-1">
              {currencySymbols[getSmarterPriceData.currency]}
              {formatNumber(getSmarterPriceData?.price)}{" "}
              {getSmarterPriceData.currency}
            </span>
            &nbsp;
          </>
        )}
        {buttonConfiguration?.showPrice && (
          <span className="elm-h6 font-bold ml-1">
            {currencySymbols[getSmarterPriceData.currency]}
            {formatNumber(discountedPrice)} {getSmarterPriceData.currency}
          </span>
        )}
      </div>
      <Button
        onClick={onClickCheckout}
        variant="brandOutline"
        className="w-full my-2"
      >
        {buttonConfiguration?.buttonText ??
          `Enroll for ${buttonConfiguration?.discountPercentage}% off`}
      </Button>

      <div className="text-left text-xs text-gray-800">
        {buttonConfiguration?.finePrint ??
          "Complete your secure checkout at GetSmarter, an edX partner."}
      </div>
    </div>
  );
}
